/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-nested-ternary */
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

import { Table } from '@/lib/components';
import { Button, IconSvg } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import { EmptyActivityIcon, EmptyContactsIcon } from '@/lib/v2/icons/general';
import { EyeIcon } from '@/lib/v2/icons/outline';
import { ChatIcon } from '@/lib/v2/icons/solid';
import { EmptyScreenTable } from '@/lib/v2/organisms';

import { getEnv } from '@/src/application/hooks/util/useEnv';
import { useAccountChatwoot } from '@/src/apps/chat/hooks/useAccountChatwoot';
import { useInboxSummaryConversation } from '@/src/apps/chat/hooks/useInboxSummaryConversation';
import configData from '@/src/config.json';
import { IInboxEvent } from '@/src/infrastructure/Protocol/Inbox/IInboxService';
import { useDate } from '@/src/infrastructure/services/useDate';

interface ITableContactProfileInboxProps {
  inboxEventList: IInboxEvent[];
  inboxEventTotalCount: number;
  changeTablePage: ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => void;
  totalPages?: number;
  isLoading?: boolean;
  gotoPageIndex?: number;
  callToAction?: [() => void, (() => void)?];
}

export function ContactsProfileInboxTable({
  inboxEventList,
  inboxEventTotalCount,
  changeTablePage,
  totalPages,
  isLoading,
  gotoPageIndex,
  callToAction,
}: ITableContactProfileInboxProps) {
  const { t } = useTranslation(['inbox', 'translation']);
  const { dateFormatter } = useDate();

  const { handleOpenSummary } = useInboxSummaryConversation();
  const { chatwootAccountId } = useAccountChatwoot();

  const conversationColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-emblue-primary">{`${t(
          'INBOX.TABLE.headers.conversation'
        )}`}</span>
      </Flex>
    ),
    accessor: 'conversation',
    id: 'conversation',
    Cell: ({ row }: { row: { original: IInboxEvent } }) => (
      <Flex withGap alignment="start">
        <IconSvg svgComponent={<ChatIcon />} />
        <span className="self-start px-1 text-[#364365]">
          <strong>{`#${row.original.conversationId}`}</strong>
        </span>
        <span className="max-w-56 self-start truncate text-[#364365] xl:max-w-64">
          {row.original.description}
        </span>
      </Flex>
    ),
  };
  const createdAtColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-emblue-primary">{`${t(
          'INBOX.TABLE.headers.createdAt'
        )}`}</span>
      </Flex>
    ),
    accessor: 'createdAt',
    id: 'createdAt',
    Cell: ({ cell: { value } }: { cell: { value: Date } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]">
          {value ? String(dateFormatter(value)) : ''}
        </span>
      </Flex>
    ),
  };
  const channelColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-emblue-primary">{`${t(
          'INBOX.TABLE.headers.channel'
        )}`}</span>
      </Flex>
    ),
    accessor: 'channel',
    id: 'channel',
    Cell: ({ cell: { value } }: { cell: { value: string } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]">{`${t(
          `INBOX.TABLE.channels.${String(value)}`
        )}`}</span>
      </Flex>
    ),
  };
  const agentColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-emblue-primary">{`${t(
          'INBOX.TABLE.headers.agent'
        )}`}</span>
      </Flex>
    ),
    accessor: 'agentName',
    id: 'agentName',
    Cell: ({ row }: { row: { original: IInboxEvent } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]">
          {row.original.agentId === null
            ? t('INBOX.TABLE.agent.unassigned')
            : row.original.agentName ?? '-'}
        </span>
      </Flex>
    ),
  };
  const statusColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-emblue-primary">{`${t(
          'INBOX.TABLE.headers.status'
        )}`}</span>
      </Flex>
    ),
    accessor: 'status',
    id: 'status',
    Cell: ({ cell: { value } }: { cell: { value: string } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]">
          {t(`INBOX.TABLE.status.${value}`, {
            defaultValue: value,
          })}
        </span>
      </Flex>
    ),
  };

  const summaryColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-emblue-primary">{`${t(
          'INBOX.TABLE.headers.summary'
        )}`}</span>
      </Flex>
    ),
    accessor: 'summary',
    id: 'summary',
    Cell: ({ row }: { row: { original: IInboxEvent } }) => {
      return (
        <Flex withGap alignment="start" className="ml-2">
          <Button
            gray
            outline
            disabled={!row.original.summary}
            id="action-eye"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              row.original.summary && void handleOpenSummary(row.original.summary);
            }}
          >
            <IconSvg height="19px" svgComponent={<EyeIcon />} width="16px" />
          </Button>
        </Flex>
      );
    },
  };

  const activityTableColumns: Column<object>[] = [
    conversationColumn,
    createdAtColumn,
    channelColumn,
    agentColumn,
    statusColumn,
    summaryColumn,
  ].filter((x) => x) as Column<object>[];

  const env = getEnv();
  const baseUrl = configData.endpoints[env === 'storybook' ? 'development' : env].URL_INBOX;

  const goToConversation = (conversationId: number) => {
    try {
      const finalUrl = `${baseUrl}/app/accounts/${chatwootAccountId}/conversations/${conversationId}`;
      window.open(finalUrl, '_blank');
    } catch (error) {
      console.error('Error getting Chatwoot account ID:', error);
    }
  };

  return (
    <div className="size-full text-base text-[#364365] 2xl:text-14">
      <Table
        // eslint-disable-next-line react/no-children-prop
        children={<></>}
        isPaginateOnOverflow
        classNamePagination="pr-4"
        columns={activityTableColumns}
        data={inboxEventList}
        emptyScreen={{
          totalCount: inboxEventTotalCount || 0,
          noResult: (
            <EmptyScreenTable
              actionText={t('INBOX.EMPTY_SCREEN_TABLE.actionText')}
              description={t('INBOX.EMPTY_SCREEN_TABLE.noResultDescription')}
              image={<EmptyContactsIcon />}
              title={t('INBOX.EMPTY_SCREEN_TABLE.noResultTitle')}
              onClickAction={callToAction && callToAction[0]}
            />
          ),
          noData: (
            <EmptyScreenTable
              actionText={t('INBOX.EMPTY_SCREEN_TABLE.actionText')}
              description={t('INBOX.EMPTY_SCREEN_TABLE.noDataDescription')}
              image={<EmptyActivityIcon />}
              title={t('INBOX.EMPTY_SCREEN_TABLE.noDataTitle')}
              onClickAction={callToAction && callToAction[0]}
            />
          ),
        }}
        gotoPageIndex={gotoPageIndex}
        isLoading={isLoading}
        withCheckbox={false}
        withPagination={{
          totalPages: totalPages,
          fetchData: changeTablePage,
        }}
        onRowClick={(value) => {
          void goToConversation(value.conversationId);
        }}
      />
    </div>
  );
}
